/**
 * 弹窗。用于"创建学生"这类可弹可页的场景。
 * 无障碍：role="dialog" + aria-modal、Esc 关闭、打开时锁滚动、打开后焦点进入弹窗。
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

export interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** 弹窗内容宽度，iPad 上不要撑满。 */
  size?: 'sm' | 'md' | 'lg';
  /**
   * 点击遮罩（弹窗外的灰色区域）是否关闭弹窗。**默认 false**。
   *
   * 为什么默认不关：
   *  1. 本应用主要在**手机 / iPad** 上用，手指误触弹窗外侧很常见；
   *  2. 有些弹窗承载**一次性信息** —— 最典型的是"孩子账号已创建"那一步，
   *     初始密码**只显示这一次**，误点一下遮罩就永久看不到了（只能去重置密码）。
   *     对这种不可恢复的后果，"点空白处就关掉"是完全不成比例的。
   *
   * 需要"点外面关掉"的场景（例如纯浏览性质的浮层）可以显式传 `closeOnBackdrop`。
   */
  closeOnBackdrop?: boolean;
}

const SIZES = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' } as const;

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = false,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  /**
   * 遮罩上的"按下"是否真的发生在遮罩本身。
   * 与 click 一起判断，避免"在弹窗内选中文字后拖到外面松手"被当成点击遮罩（见下方注释）。
   */
  const backdropPressedRef = useRef(false);

  /**
   * `onClose` 放进 ref，**不作为 effect 依赖**。
   *
   * ⚠️ 这是一个真实踩过的 bug：调用方几乎都传内联箭头函数
   *   （如 `onClose={() => setOpen(false)}` 或组件内 `const close = () => {...}`），
   *   它的身份**每次渲染都变**。若把它列进依赖数组，那么弹窗里每敲一个字符：
   *   输入 → setState → 父组件重渲染 → onClose 变新身份 → effect 重跑
   *   → 里面的 `panelRef.current.focus()` 把焦点从 input 抢回弹窗容器
   *   → **输入框立刻失焦**，用户打一个字就要重新点一次。
   *
   *   修在这里而不是逐个调用方加 `useCallback`：只要有任何一处忘了包，
   *   对应弹窗的所有输入框就全都不可用，而这种 bug 只在"打字"时才暴露。
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // 只在"焦点还不在弹窗内"时才把焦点移进来。
    // 这道护栏有两重作用：
    //   1) 不打断正在输入的用户（焦点已在某个 input 上就绝不动它）
    //   2) 万一将来有人又把本 effect 的依赖写宽，也不会抢走输入焦点
    if (!panelRef.current?.contains(document.activeElement)) {
      panelRef.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]); // ← 刻意只依赖 open：仅"打开/关闭"时执行一次

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto sm:items-center">
      {/*
        遮罩只在**显式开启** closeOnBackdrop 时才响应点击（默认不关，见 ModalProps 注释）。
        另外还要求"按下与松开都发生在遮罩上"，这挡掉一个很隐蔽的误关路径：
        在弹窗内选中文字后把鼠标拖到外面松手 —— 此时 click 事件会派发到
        mousedown 与 mouseup 两个目标的**共同祖先**，也就是这个遮罩，
        于是"只是想选中一段文字"就把弹窗关掉了。
      */}
      <div
        className="absolute inset-0 bg-slate-900/40"
        onPointerDown={(event) => {
          backdropPressedRef.current = event.target === event.currentTarget;
        }}
        onClick={(event) => {
          if (!closeOnBackdrop) return;
          if (!backdropPressedRef.current) return;
          if (event.target !== event.currentTarget) return;
          onCloseRef.current();
        }}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          /*
            ⚠️ 高度用 **dvh**（2026-11-04 用户报的："手机上操作，发现上面一部分内容
            看不到"）。`vh` 是**布局视口**，手机浏览器把地址栏/工具栏算在内，
            于是 `92vh` 会比真正能看见的区域更高 —— 弹窗顶部（标题、第一行说明）
            被顶到屏幕外，而且外层不滚动，根本够不着。

            `dvh` = **动态视口**（地址栏出现/隐藏时跟着变），再配一个极小的
            上下留白，弹窗就一定能完整落在可见区域内。
            外层同时允许滚动，作为极端情况（如横屏 + 键盘弹出）的兜底。
          */
          'relative z-10 flex max-h-[calc(100dvh-0.75rem)] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl',
          'sm:max-h-[92dvh] sm:rounded-2xl',
          SIZES[size],
        )}
      >
        {/*
          标题栏**不参与滚动**（2026-11-04 用户报的："固定的标题栏初始时好像有点
          盖住下面的'收录在'"）。

          上一版用 `position: sticky` 吸顶，滚动区里有个"负外边距 + 吸顶"的组合，
          首屏时标题栏会压住第一个子元素几个像素。现在改成**结构上分开**：
          面板是一个 flex 列 —— 标题栏固定高度、正文自己滚。没有 sticky，
          也就不存在"盖住"。
        */}
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭"
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              ✕
            </span>
          </button>
        </div>
        {/* 正文：只有这一块滚动（`overscroll-contain` 防止滚到底后带动页面）。 */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
          {children}
          {footer && <div className="mt-5 flex justify-end gap-2">{footer}</div>}
        </div>
      </div>
    </div>,
    document.body,
  );
}
